import React from 'react';
import { useHasPermission } from '../../utils/permissions';

/**
 * PermissionGate component to conditionally render content based on user permissions
 * 
 * @param {string|array} require - Permission(s) required to access the content
 * @param {string} mode - 'any' (default) or 'all' - determines if user needs any or all permissions
 * @param {React.ReactNode} fallback - Component to render if permission is denied (default: null)
 * @param {React.ReactNode} children - Content to render if permission is granted
 * @param {React.ReactNode} unauthorized - Alternative content for when user lacks permission
 * @param {string} className - CSS class to apply to wrapper
 * @param {boolean} showFallback - Whether to show fallback content (default: true)
 * 
 * @example
 * // Simple usage
 * <PermissionGate require="view_products">
 *   <ProductsList />
 * </PermissionGate>
 * 
 * @example
 * // Multiple permissions (any)
 * <PermissionGate require={["add_products", "edit_products"]}>
 *   <ProductForm />
 * </PermissionGate>
 * 
 * @example
 * // Multiple permissions (all required)
 * <PermissionGate require={["view_products", "edit_products"]} mode="all">
 *   <ProductEditButton />
 * </PermissionGate>
 * 
 * @example
 * // With custom fallback
 * <PermissionGate 
 *   require="view_restricted_data" 
 *   fallback={<div>Access denied</div>}
 * >
 *   <RestrictedData />
 * </PermissionGate>
 */
const PermissionGate = ({ 
  require, 
  mode = 'any', 
  fallback = null, 
  children, 
  unauthorized,
  className,
  showFallback = true 
}) => {
  const allowed = useHasPermission(require, { mode });
  
  if (!allowed) {
    if (unauthorized) {
      return <>{unauthorized}</>;
    }
    return showFallback ? fallback : null;
  }
  
  if (className) {
    return <div className={className}>{children}</div>;
  }
  
  return <>{children}</>;
};

export default PermissionGate;